/**
 * GrateElevationsConfirmation Component
 * Review/confirmation page for grate elevations section
 */
import React, { useState } from "react";
import FilePreviewModalSimple from "../FileUpload/FilePreviewModalSimple";
import LabeledImageUpload from "./LabeledImageUpload";

const GrateElevationsConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
  onDataUpdate,
  projectId,
  clientId,
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    // Filter out null/undefined/empty values from the data object
    const filteredData = {};
    Object.entries(data).forEach(([key, value]) => {
      // Skip N/A flag fields
      if (key.includes("_na")) return;

      // Skip fields where corresponding _na field is true
      const naFieldName = `${key}_na`;
      if (currentData[naFieldName] === true) return;

      // Skip fields with 'N/A' value
      if (value === "N/A") return;

      // Skip null, undefined, or empty string values
      if (value === null || value === undefined || value === "") return;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return;

      // Include the value
      filteredData[key] = value;
    });

    if (Object.keys(filteredData).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(filteredData).map(([key, value]) => {
            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }

            // Render editable image upload component
            if (isImageField) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <LabeledImageUpload
                    images={value || []}
                    onImagesChange={(updatedImages) => {
                      if (onDataUpdate) {
                        onDataUpdate(key, updatedImages);
                      }
                    }}
                    projectId={projectId}
                    clientId={clientId}
                    hideUploadBox={true}
                  />
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "200px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="grate-elevations-confirmation" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        <h3
          style={{
            margin: "0 0 25px 0",
            fontSize: "20px",
            paddingBottom: "15px",
            borderBottom: "2px solid var(--border-secondary)",
          }}
        >
          Grate Elevations
        </h3>

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Are there catch basins on the project?",
            currentData.has_catch_basins,
            "has_catch_basins",
          )}
          {renderYesNoAnswer(
            "Are there elevations in plan view?",
            currentData.has_plan_view_elevations,
            "has_plan_view_elevations",
          )}
          {renderYesNoAnswer(
            "Are there elevations in profile view?",
            currentData.has_profile_view_elevations,
            "has_profile_view_elevations",
          )}
          {renderYesNoAnswer(
            "Is there a grate elevation table?",
            currentData.has_elevation_table,
            "has_elevation_table",
          )}
        </div>

        {/* Plan View Data */}
        {currentData.has_plan_view_elevations &&
          renderDataSection(
            "Grate Elevations - Plan View",
            {
              plan_view_images: currentData.plan_view_images,
            },
            "plan_view_form",
          )}

        {/* Profile View Data */}
        {currentData.has_profile_view_elevations &&
          renderDataSection(
            "Grate Elevations - Profile View",
            {
              profile_view_images: currentData.profile_view_images,
            },
            "profile_view_form",
          )}

        {/* Elevation Table Data */}
        {currentData.has_elevation_table &&
          renderDataSection(
            "Grate Elevation Table",
            {
              elevation_table_images: currentData.elevation_table_images,
            },
            "elevation_table_form",
          )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted && (
          <button
            onClick={onConfirm}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        )}
      </div>
      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default GrateElevationsConfirmation;
